import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight, Download, ExternalLink, Eye, Pencil, Trash2, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import type { Page } from '@/shared/http';
import { defineBlockComponent, type BlockComponentProps } from '@/shared/page';
import { Button } from '@/shared/ui/button';

import { columnSpecSchema, mobileRoles } from './columns';
import { DataView, type DataViewProps } from './data-view';
import { readPath, resolvePatternText, textSchema } from './text';

/**
 * Props do `DataView` como genérico de bloco: tudo JSON (colunas por preset, ações por id), para o
 * bloco de configuração e a página personalizada (08 §4.3). As intenções vão ao hospedeiro (U41):
 * `openAction` com `{ id }`, `pageAction` com `{ page, size, orderBy? }` (página a partir de 0) e cada
 * ação de lote com `{ ids }`.
 */
/** Ícones das ações de linha por nome, para a configuração ser JSON (lista fechada, como `STATUS_ICONS`). */
export const ROW_ACTION_ICONS = {
  pencil: Pencil,
  'trash-2': Trash2,
  eye: Eye,
  'external-link': ExternalLink,
  'arrow-up-right': ArrowUpRight,
  download: Download,
} as const satisfies Readonly<Record<string, LucideIcon>>;
const ROW_ICON_NAMES = Object.keys(ROW_ACTION_ICONS) as [
  keyof typeof ROW_ACTION_ICONS,
  ...(keyof typeof ROW_ACTION_ICONS)[],
];

export const dataViewBlockProps = z.object({
  columns: z.array(columnSpecSchema).min(1).max(40),
  rowKey: z.string().min(1),
  /** Nome acessível da tabela/lista. */
  label: textSchema,
  page: z.number().int().min(0),
  size: z.number().int().min(1).max(100),
  orderBy: z.string().optional(),
  sizes: z.array(z.number().int().min(1).max(100)).max(6).optional(),
  openAction: z.string().optional(),
  pageAction: z.string().optional(),
  bulkActions: z
    .array(
      z.object({
        id: z.string(),
        label: textSchema,
        tone: z.enum(['default', 'danger']).optional(),
      }),
    )
    .max(4)
    .optional(),
  /** Ações por linha (lápis, lixeira): cada uma pede `{ id, name }` ao hospedeiro (o `name` é o título do cartão). */
  rowActions: z
    .array(
      z.object({
        id: z.string(),
        label: textSchema,
        icon: z.enum(ROW_ICON_NAMES).optional(),
        tone: z.enum(['default', 'danger']).optional(),
      }),
    )
    .max(4)
    .optional(),
});

export type DataViewBlockProps = z.infer<typeof dataViewBlockProps>;
type View = BlockComponentProps<DataViewBlockProps, Page<unknown>>;

function useBlockDataView(
  { props, data, run, canRun }: View,
  presentation: 'web' | 'mobile',
): DataViewProps<unknown> {
  const { i18n } = useLingui();
  const [selected, setSelected] = useState<readonly string[]>([]);
  const bulk = (props.bulkActions ?? []).filter((action) => canRun(action.id));
  const { openAction, pageAction } = props;
  const rowActions = (props.rowActions ?? []).filter((action) => canRun(action.id));
  const titleColumn = props.columns[mobileRoles(props.columns).indexOf('title')];
  const paginate = (next: { page: number; size: number; orderBy: string | undefined }) => {
    if (!pageAction) return;
    setSelected([]);
    run(pageAction, next.orderBy ? next : { page: next.page, size: next.size });
  };
  return {
    columns: props.columns,
    page: data,
    rowKey: props.rowKey,
    label: resolvePatternText(i18n, props.label),
    presentation,
    framed: false,
    ...(openAction && canRun(openAction)
      ? { onOpen: (row: unknown) => run(openAction, { id: String(readPath(row, props.rowKey)) }) }
      : {}),
    ...(pageAction && canRun(pageAction)
      ? {
          pagination: {
            pageIndex: props.page,
            pageSize: props.size,
            ...(props.sizes ? { sizes: props.sizes } : {}),
            onChange: ({ pageIndex, pageSize }) =>
              paginate({ page: pageIndex, size: pageSize, orderBy: props.orderBy }),
          },
          sort: {
            orderBy: props.orderBy,
            onChange: (orderBy) => paginate({ page: 0, size: props.size, orderBy }),
          },
        }
      : {}),
    ...(rowActions.length > 0
      ? {
          rowActions: (row: unknown) =>
            rowActions.map((action) => {
              const name = titleColumn ? readPath(row, titleColumn.field) : undefined;
              return {
                id: action.id,
                label: resolvePatternText(i18n, action.label),
                icon: action.icon ? ROW_ACTION_ICONS[action.icon] : undefined,
                tone: action.tone,
                onSelect: () =>
                  run(action.id, {
                    id: String(readPath(row, props.rowKey)),
                    ...(typeof name === 'string' ? { name } : {}),
                  }),
              };
            }),
        }
      : {}),
    ...(bulk.length > 0
      ? {
          selection: {
            selected,
            onChange: setSelected,
            actions: (ids) =>
              bulk.map((action) => (
                <Button
                  key={action.id}
                  size="sm"
                  variant={action.tone === 'danger' ? 'danger' : 'secondary'}
                  onClick={() => run(action.id, { ids: [...ids] })}
                >
                  {resolvePatternText(i18n, action.label)}
                </Button>
              )),
          },
        }
      : {}),
  };
}

function DataViewBlockWeb(view: View) {
  return <DataView {...useBlockDataView(view, 'web')} />;
}

function DataViewBlockMobile(view: View) {
  return <DataView {...useBlockDataView(view, 'mobile')} />;
}

/** `DataView` como genérico do catálogo: `defineConfigBlock(DataViewBlock)({ … })` sobre uma fonte `page`. */
export const DataViewBlock = defineBlockComponent<DataViewBlockProps, Page<unknown>>({
  id: 'DataView',
  description:
    'Paged list with typed column presets: table on web, dedicated cards on iPhone; server paging, sort and optional bulk selection.',
  props: dataViewBlockProps,
  render: { web: DataViewBlockWeb, mobile: DataViewBlockMobile },
});
